import { paths } from '@/api/openapi/schema';
import { CANVAS_SIZE, FONT_FAMILIES } from '@/constant';
import { SlideComponentDndCreator } from '@/providers/editor/creators/SlideComponentDnd.creator';
import { SlideComponentTextCreator } from '@/providers/editor/creators/SlideComponentText.creator';
import { SlideComponent, SlideComponentDnd, SlideComponentDndDragZone, SlideComponentDndDropZone, SlideComponentText } from '@/types/slide';

const TRADITIONAL_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZHKXinBiaoKai')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;
const SIMPLIFIED_FONT_FAMILY = FONT_FAMILIES.find((f) => f.fontFamily === 'FZKai-Z03S')!.fontFamily ?? FONT_FAMILIES[0]?.fontFamily;

const getImageSize = async (url: string) => {
    return new Promise<{ height: number; width: number }>((resolve, reject) => {
        const img = new Image();
        img.src = url;
        img.onload = () => resolve({ height: img.height, width: img.width });
        img.onerror = (error) => reject(error);
    });
};

export const generate = async (input?: paths['/experiments/generators/questions/demo/matching']['post']['responses']['200']['content']['application/json']): Promise<SlideComponent[][]> => {
    if (!input || input.question?.type !== 'matching' || !input.question.content) {
        console.log('Invalid input for matching question generation:', input);
        return [];
    }

    const slides: SlideComponent[][] = [];
    const components: SlideComponent[] = [];

    const margin = 16;

    /**
     * Create the question text component
     */
    const text: SlideComponentText = new SlideComponentTextCreator().create(crypto.randomUUID());
    text.content.text = input.question.content.question_text;
    text.pos = { x: margin, y: margin };
    text.size = { width: CANVAS_SIZE.width - margin * 2, height: 160 };
    text.content.typeSettings.fontFamily = input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY;
    text.content.fontSize = 48;

    /**
     * Create the drag and drop components
     */
    const dnd: SlideComponentDnd = new SlideComponentDndCreator().create(crypto.randomUUID());
    const width = 152;
    const height = 152;
    const dropZones: SlideComponentDndDropZone[] = [];
    const dragZones: SlideComponentDndDragZone[] = [];

    for (const [index, match] of input.question.content.matches.entries()) {
        try {
            const size =
                match.matching_object.object_type === 'image'
                    ? await (async () => {
                          const actulSize = await getImageSize(match.matching_object.object_value);
                          return { width: width, height: (actulSize.height / actulSize.width) * height };
                      })()
                    : { height, width };

            const dropZone: SlideComponentDndDropZone =
                match.matching_object.object_type === 'text'
                    ? {
                          id: crypto.randomUUID(),
                          type: 'text',
                          pos: { x: 0, y: 480 },
                          size,
                          settings: { backgroundColor: '#DADEE7', multiple: true },
                          tag: index.toString(),
                          connectIds: [],
                          content: {
                              text: match.matching_object.object_value,
                              fontSize: 24,
                              jyutping: [],
                              color: '#000000',
                              typeSettings: {
                                  fontFamily: input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                                  formatting: [],
                                  alignment: 'center',
                                  isPhonetic: false,
                                  borderRadius: 0,
                                  backgroundColor: 'transparent',
                              },
                          },
                      }
                    : {
                          id: crypto.randomUUID(),
                          type: 'image',
                          pos: { x: 0, y: 480 },
                          size,
                          settings: { backgroundColor: '#DADEE7', multiple: true },
                          tag: index.toString(),
                          connectIds: [],
                          content: {
                              image: '',
                              imageURL: match.matching_object.object_value,
                              flip: 1,
                          },
                      };

            for (const answer of match.matching_answers) {
                try {
                    const size =
                        answer.answer_type === 'image'
                            ? await (async () => {
                                  const actulSize = await getImageSize(answer.answer_value);
                                  return { width: width, height: (actulSize.height / actulSize.width) * height };
                              })()
                            : { height, width };

                    const dragZone: SlideComponentDndDragZone =
                        answer.answer_type === 'text'
                            ? {
                                  id: crypto.randomUUID(),
                                  type: 'text',
                                  pos: { x: 0, y: 200 },
                                  size: { width, height },
                                  settings: { backgroundColor: '#DADEE7' },
                                  tag: index.toString(),
                                  content: {
                                      text: answer.answer_value,
                                      fontSize: 24,
                                      jyutping: [],
                                      color: '#000000',
                                      typeSettings: {
                                          fontFamily: input.question.content.language_variant === 'zh-Hant' ? TRADITIONAL_FONT_FAMILY : SIMPLIFIED_FONT_FAMILY,
                                          formatting: [],
                                          alignment: 'center',
                                          isPhonetic: false,
                                          borderRadius: 0,
                                          backgroundColor: 'transparent',
                                      },
                                  },
                              }
                            : {
                                  id: crypto.randomUUID(),
                                  type: 'image',
                                  pos: { x: 0, y: 200 },
                                  size: size,
                                  settings: { backgroundColor: '#DADEE7' },
                                  tag: index.toString(),
                                  content: {
                                      image: '',
                                      imageURL: answer.answer_value,
                                      flip: 1,
                                  },
                              };
                    dropZone.connectIds.push(dragZone.id);
                    dragZones.push(dragZone);
                } catch (error) {
                    console.warn('Error processing answer:', error);
                    continue; // Skip this answer if there's an error
                }
            }

            dropZones.push(dropZone);
        } catch (error) {
            console.warn('Error processing matching object:', error);
            continue; // Skip this matching if there's an error
        }
    }

    dragZones.forEach((zone, i) => {
        // Assign a unique position for each drag zone
        zone.pos = { x: (1024 / dragZones.length) * (i + 1) - 1024 / dragZones.length / 2 - width / 2, y: zone.pos.y };
    });

    dropZones.forEach((zone, i) => {
        // Assign a unique position for each drop zone
        zone.pos = { x: (1024 / dropZones.length) * (i + 1) - 1024 / dropZones.length / 2 - width / 2, y: zone.pos.y };
    });

    dnd.content.dragZone = dragZones;
    dnd.content.dropZone = dropZones;

    // make sure the dnd is below the text
    components.push(dnd);
    components.push(text);

    slides.push(components);

    return slides;
};
